import {Autocomplete, AutocompleteProps, CircularProgress, TextField} from "@mui/material";
import {skipToken} from "@reduxjs/toolkit/dist/query";
import {isArray} from "lodash";
import React, {useState} from "react";
import {BaseModelType, ListQuery, ListRequest} from "../../../app/api/types";
import {Filter, FilterOperator} from "../../ExTable";

type OMIT_FROM_PROPS =
    | "open"
    | "onOpen"
    | "onClose"
    | "value"
    | "onChange"
    | "isOptionEqualToValue"
    | "multiple"
    | "getOptionLabel"
    | "onInputChange"
    | "options"
    | "loading"
    | "renderInput";

export interface BasicAsyncAutocompleteProps<T extends BaseModelType, U extends ListRequest>
    extends Omit<AutocompleteProps<T, boolean, boolean, boolean>, OMIT_FROM_PROPS> {
    name?: string;
    queryHook: ListQuery<T, U>;
    additionalRequestParams: Omit<U, "params">;
    additionalFilters?: Filter[];
    idField?: keyof T;
    queryField: keyof T;
    label: string;
    getOptionLabel?: (option: string | T) => string;
    value?: T | string | number;
    onChange?: (value?: T) => void;
    sort?: string;
}

export default function BasicAsyncAutocomplete<T extends BaseModelType, U extends ListRequest>(
    props: BasicAsyncAutocompleteProps<T, U>
) {
    const {
        name,
        queryHook,
        additionalRequestParams,
        additionalFilters = [],
        queryField,
        label,
        getOptionLabel = (option) => (typeof option === "string" ? option : String(option[queryField])),
        idField = "id",
        value,
        onChange,
        sort,
        ...rest
    } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const {data: options, isLoading} = queryHook(
        !searchQuery && !isOpen
            ? skipToken
            : ({
                  ...additionalRequestParams,
                  params: {
                      pageSize: 100,
                      offset: 0,
                      sort: sort || queryField,
                      filter: JSON.stringify(
                          searchQuery
                              ? [...additionalFilters, {f: queryField, o: FilterOperator.CONTAINS, v: searchQuery}]
                              : additionalFilters
                      ),
                  },
              } as U)
    );

    const {data: retrievedValue} = queryHook(
        typeof value === "string" || typeof value === "number"
            ? ({
                  ...additionalRequestParams,
                  params: {
                      pageSize: 1,
                      offset: 0,
                      sort: idField,
                      filter: JSON.stringify([{f: idField, o: FilterOperator.EQ, v: value}]),
                  },
              } as U)
            : skipToken
    );

    return (
        <Autocomplete
            open={isOpen}
            onOpen={() => {
                setIsOpen(true);
            }}
            onClose={() => {
                setIsOpen(false);
            }}
            value={
                (typeof value === "string" || typeof value === "number" ? retrievedValue?.payload[0] : value) || null
            }
            onChange={(_, newValue) => {
                if (isArray(newValue)) {
                    onChange?.(newValue[0] as T);
                } else {
                    onChange?.(newValue as T);
                }
            }}
            isOptionEqualToValue={(option, curValue) =>
                //@ts-expect-error
                option[idField] === curValue[idField] || JSON.stringify(option[idField]) === curValue
            }
            getOptionLabel={getOptionLabel}
            onInputChange={(_, inputValue) => setSearchQuery(inputValue)}
            options={prefilterOptions(options?.payload, queryField) || ([] as T[])}
            loading={isLoading}
            renderInput={(params) => (
                <TextField
                    {...params}
                    name={name}
                    label={label}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
            {...rest}
        />
    );
}

function prefilterOptions<T extends BaseModelType, K extends keyof T>(options: T[] | undefined, queryField: K): T[] {
    if (!options) return [];
    return options.filter((opt) => opt?.[queryField] != null);
}
